import { useCallback, useRef } from 'react';
import * as Cesium from 'cesium';
import type { Satellite, SensorData } from '../../../types/mission';
import { SENSOR_BEAM_COLOR, RECT_SENSOR_BEAM_COLOR } from '../constants';

function beamPulse(phase = 0, durationMs = 1800): number {
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  return 0.5 + Math.sin((now / durationMs + phase) * Math.PI * 2) * 0.5;
}

function animatedBeamMaterial(
  color: Cesium.Color,
  minAlpha: number,
  maxAlpha: number,
  phase = 0,
): Cesium.ColorMaterialProperty {
  return new Cesium.ColorMaterialProperty(new Cesium.CallbackProperty(() => (
    color.withAlpha(minAlpha + (maxAlpha - minAlpha) * beamPulse(phase))
  ), false));
}

export function useSensorFov(
  viewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  secondaryViewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  sharedDataSourceRef: React.MutableRefObject<any>,
  previewDataSourceRef: React.MutableRefObject<any>,
  entityGroupMap: React.MutableRefObject<Map<string, string[]>>,
  satOrbitActiveMap: React.MutableRefObject<Record<string, boolean>>,
  layersRef: React.MutableRefObject<any>,
  clearGroup: (group: string) => void,
) {
  function satFixedPos(satEntity: Cesium.Entity, time: Cesium.JulianDate): Cesium.Cartesian3 | null {
    const ecef = satEntity.position?.getValue(time);
    return ecef ?? null;
  }

  function computeSemiAxis(satEntity: Cesium.Entity, time: Cesium.JulianDate, halfAngleDeg: number): number {
    const fixed = satFixedPos(satEntity, time);
    if (!fixed) return 1_000;
    const carto = Cesium.Cartographic.fromCartesian(fixed);
    if (!carto || carto.height < 1000) return 1_000;
    const altKm = Math.max(carto.height / 1000, 1);
    const angle = Math.min(halfAngleDeg, 85);
    return altKm * 1_000 * Math.tan(Cesium.Math.toRadians(angle));
  }

  const drawSensorFovs = useCallback((sats: Satellite[]) => {
    const v = viewerRef.current;
    const ds = sharedDataSourceRef.current;
    if (!v || !ds) return;

    sats.forEach((sat) => {
      if (!sat.sensors?.length) return;

      sat.sensors.forEach((sensor: SensorData, idx: number) => {
        const color = sensor.geometry === 'rectangular' ? RECT_SENSOR_BEAM_COLOR : SENSOR_BEAM_COLOR;
        const pulsePhase = idx * 0.17;
        const coneMaterial = animatedBeamMaterial(color, 0.24, 0.52, pulsePhase);
        const footprintMaterial = animatedBeamMaterial(color, 0.22, 0.48, pulsePhase + 0.18);
        const satId = sat.id;
        const fovId = `sfov_${satId}_${idx}`;

        let halfH = 5;
        let halfV = 5;
        if (sensor.geometry === 'conical') {
          const raw = parseFloat(sensor.fov);
          const val = isNaN(raw) || raw <= 0 ? 5 : raw / 2;
          halfH = halfV = Math.min(val, 85);
        } else {
          const rawH = parseFloat(sensor.fovH);
          const rawV = parseFloat(sensor.fovV);
          halfH = Math.min(isNaN(rawH) || rawH <= 0 ? 5 : rawH / 2, 85);
          halfV = Math.min(isNaN(rawV) || rawV <= 0 ? 5 : rawV / 2, 85);
        }
        const halfMajor = Math.max(halfH, halfV);
        const halfMinor = Math.min(halfH, halfV);

        const getSatCarto = (time: Cesium.JulianDate): Cesium.Cartographic | null => {
          const e = sharedDataSourceRef.current?.entities.getById(satId);
          if (!e) return null;
          const fp = satFixedPos(e, time);
          if (!fp) return null;
          const c = Cesium.Cartographic.fromCartesian(fp);
          return (isFinite(c.longitude) && isFinite(c.latitude) && isFinite(c.height)) ? c : null;
        };

        const lastCartoCache = {
          time: null as Cesium.JulianDate | null,
          carto: null as Cesium.Cartographic | null,
        };

        const getSatCartoCached = (time: Cesium.JulianDate): Cesium.Cartographic | null => {
          if (
            lastCartoCache.time &&
            lastCartoCache.time.dayNumber === time.dayNumber &&
            lastCartoCache.time.secondsOfDay === time.secondsOfDay
          ) {
            return lastCartoCache.carto;
          }
          const c = getSatCarto(time);
          lastCartoCache.time = Cesium.JulianDate.clone(time);
          lastCartoCache.carto = c;
          return c;
        };

        const safeInitialNadir = (() => {
          const ic = getSatCarto(v.clock.currentTime);
          if (ic) return Cesium.Cartesian3.fromRadians(ic.longitude, ic.latitude, 0);
          return Cesium.Cartesian3.fromDegrees(0, 0, 0);
        })();

        const R_EARTH_M = 6_371_000;
        const showCb = new Cesium.CallbackProperty(
          () => layersRef.current.sensorFovs && satOrbitActiveMap.current[satId] !== false,
          false,
        );

        const arcLenM = (altKm: number, halfDeg: number): number =>
          altKm * 1_000 * Math.tan(Cesium.Math.toRadians(halfDeg));

        const N_SLICES = sensor.geometry === 'rectangular' ? 4 : 12;

        const getPerimOpen = (satEcef: Cesium.Cartesian3): Cesium.Cartesian3[] => {
          const c2 = Cesium.Cartographic.fromCartesian(satEcef);
          if (!c2 || !isFinite(c2.latitude) || !isFinite(c2.longitude) || c2.height < 1000) return [];
          
          const nadirEcef = Cesium.Cartesian3.fromRadians(c2.longitude, c2.latitude, 0);
          const enuMatrix = Cesium.Transforms.eastNorthUpToFixedFrame(nadirEcef);
          
          const altKm = Math.max(c2.height / 1000, 1);
          const pts: Cesium.Cartesian3[] = [];
          if (sensor.geometry === 'rectangular') {
            const hw = arcLenM(altKm, halfH);
            const hh = arcLenM(altKm, halfV);
            const localOffsets = [
              new Cesium.Cartesian3(hw, hh, 0),
              new Cesium.Cartesian3(-hw, hh, 0),
              new Cesium.Cartesian3(-hw, -hh, 0),
              new Cesium.Cartesian3(hw, -hh, 0)
            ];
            localOffsets.forEach(offset => {
              const pt = Cesium.Matrix4.multiplyByPoint(enuMatrix, offset, new Cesium.Cartesian3());
              pts.push(pt);
            });
          } else {
            const rH = arcLenM(altKm, halfH);
            const rV = arcLenM(altKm, halfV);
            for (let pi = 0; pi < N_SLICES; pi++) {
              const a = (pi / N_SLICES) * Math.PI * 2;
              const localOffset = new Cesium.Cartesian3(rH * Math.sin(a), rV * Math.cos(a), 0);
              const pt = Cesium.Matrix4.multiplyByPoint(enuMatrix, localOffset, new Cesium.Cartesian3());
              pts.push(pt);
            }
          }
          return pts;
        };

        const lastPerimCache = {
          time: null as Cesium.JulianDate | null,
          pts: [] as Cesium.Cartesian3[],
        };

        const getPerimOpenCached = (satEcef: Cesium.Cartesian3, time: Cesium.JulianDate): Cesium.Cartesian3[] => {
          if (
            lastPerimCache.time &&
            lastPerimCache.time.dayNumber === time.dayNumber &&
            lastPerimCache.time.secondsOfDay === time.secondsOfDay
          ) {
            return lastPerimCache.pts;
          }
          const pts = getPerimOpen(satEcef);
          lastPerimCache.time = Cesium.JulianDate.clone(time, lastPerimCache.time || undefined);
          lastPerimCache.pts = pts;
          return pts;
        };

        const getPerimClosed = (satEcef: Cesium.Cartesian3, time: Cesium.JulianDate): Cesium.Cartesian3[] => {
          const pts = getPerimOpenCached(satEcef, time);
          if (pts.length < 2) return [];
          return [...pts, pts[0]];
        };

        const faceHierarchyCache: Array<{
          time: Cesium.JulianDate | null;
          hierarchy: Cesium.PolygonHierarchy;
        }> = Array.from({ length: N_SLICES }, () => ({
          time: null,
          hierarchy: new Cesium.PolygonHierarchy([]),
        }));

        for (let si = 0; si < N_SLICES; si++) {
          ds.entities.add({
            id: `${fovId}_face_${si}`,
            polygon: {
              hierarchy: new Cesium.CallbackProperty((time: Cesium.JulianDate) => {
                const cache = faceHierarchyCache[si];
                if (
                  cache.time &&
                  cache.time.dayNumber === time.dayNumber &&
                  cache.time.secondsOfDay === time.secondsOfDay
                ) {
                  return cache.hierarchy;
                }
                const e2 = sharedDataSourceRef.current?.entities.getById(satId);
                if (!e2) return new Cesium.PolygonHierarchy([]);
                const satEcef = satFixedPos(e2, time);
                if (!satEcef) return new Cesium.PolygonHierarchy([]);
                const perim = getPerimOpenCached(satEcef, time);
                if (!perim || perim.length === 0) return new Cesium.PolygonHierarchy([]);
                const p1 = perim[si % perim.length];
                const p2 = perim[(si + 1) % perim.length];
                if (!p1 || !p2) return new Cesium.PolygonHierarchy([]);
                
                cache.time = Cesium.JulianDate.clone(time, cache.time || undefined);
                cache.hierarchy.positions = [satEcef, p1, p2];
                return cache.hierarchy;
              }, false) as unknown as Cesium.PolygonHierarchy,
              perPositionHeight: true,
              material: coneMaterial,
              outline: false,
              show: showCb,
            },
          });
        }

        let cachedSemiMajor = 0;
        let cachedSemiMinor = 0;
        const getSemiMajor = (satEntity: Cesium.Entity, time: Cesium.JulianDate) => {
          if (cachedSemiMajor > 0) return cachedSemiMajor;
          const val = computeSemiAxis(satEntity, time, halfMajor);
          if (val > 1000) cachedSemiMajor = val;
          return val;
        };
        const getSemiMinor = (satEntity: Cesium.Entity, time: Cesium.JulianDate) => {
          if (cachedSemiMinor > 0) return cachedSemiMinor;
          const val = computeSemiAxis(satEntity, time, halfMinor);
          if (val > 1000) cachedSemiMinor = val;
          return val;
        };

        const lastRectCache = {
          time: null as Cesium.JulianDate | null,
          rect: new Cesium.Rectangle(),
        };

        if (sensor.geometry === 'rectangular') {
          const clampRect = (lon: number, lat: number, dLon: number, dLat: number) =>
            new Cesium.Rectangle(
              Math.max(lon - dLon, -Math.PI), Math.max(lat - dLat, -Math.PI / 2),
              Math.min(lon + dLon,  Math.PI),  Math.min(lat + dLat,  Math.PI / 2),
            );
          ds.entities.add({
            id: fovId,
            rectangle: {
              coordinates: new Cesium.CallbackProperty((time: Cesium.JulianDate) => {
                if (
                  lastRectCache.time &&
                  lastRectCache.time.dayNumber === time.dayNumber &&
                  lastRectCache.time.secondsOfDay === time.secondsOfDay
                ) {
                  return lastRectCache.rect;
                }
                const c = getSatCartoCached(time);
                if (!c) return clampRect(0, 0, 0.001, 0.001);
                const a = Math.max(c.height / 1000, 1);
                const dLon = arcLenM(a, halfH) / (R_EARTH_M * Math.max(Math.cos(c.latitude), 0.001));
                const dLat = arcLenM(a, halfV) / R_EARTH_M;
                
                lastRectCache.time = Cesium.JulianDate.clone(time, lastRectCache.time || undefined);
                lastRectCache.rect.west = Math.max(c.longitude - dLon, -Math.PI);
                lastRectCache.rect.south = Math.max(c.latitude - dLat, -Math.PI / 2);
                lastRectCache.rect.east = Math.min(c.longitude + dLon, Math.PI);
                lastRectCache.rect.north = Math.min(c.latitude + dLat, Math.PI / 2);
                return lastRectCache.rect;
              }, false),
              material: footprintMaterial,
              outline: false,
              classificationType: Cesium.ClassificationType.TERRAIN,
              show: showCb,
            },
          });
        } else {
          const lastNadirCache = {
            time: null as Cesium.JulianDate | null,
            pos: new Cesium.Cartesian3(),
          };

          ds.entities.add({
            id: fovId,
            position: new Cesium.CallbackProperty((time: Cesium.JulianDate) => {
              if (
                lastNadirCache.time &&
                lastNadirCache.time.dayNumber === time.dayNumber &&
                lastNadirCache.time.secondsOfDay === time.secondsOfDay
              ) {
                return lastNadirCache.pos;
              }
              const c = getSatCartoCached(time);
              if (!c) return safeInitialNadir;
              const pos = Cesium.Cartesian3.fromRadians(c.longitude, c.latitude, 0, undefined, lastNadirCache.pos);
              lastNadirCache.time = Cesium.JulianDate.clone(time, lastNadirCache.time || undefined);
              lastNadirCache.pos = pos;
              return pos;
            }, false) as unknown as Cesium.PositionProperty,
            ellipse: {
              semiMajorAxis: new Cesium.CallbackProperty((time: Cesium.JulianDate) => {
                const e = sharedDataSourceRef.current?.entities.getById(satId);
                if (!e) return 1000;
                return getSemiMajor(e, time);
              }, false),
              semiMinorAxis: new Cesium.CallbackProperty((time: Cesium.JulianDate) => {
                const e = sharedDataSourceRef.current?.entities.getById(satId);
                if (!e) return 1000;
                return getSemiMinor(e, time);
              }, false),
              material: footprintMaterial,
              outline: false,
              classificationType: Cesium.ClassificationType.TERRAIN,
              show: showCb,
            },
          });
        }

        const lastOutlineCache = {
          time: null as Cesium.JulianDate | null,
          pts: [] as Cesium.Cartesian3[],
        };

        ds.entities.add({
          id: `${fovId}_outline`,
          polyline: {
            arcType: Cesium.ArcType.GEODESIC,
            positions: new Cesium.CallbackProperty((time: Cesium.JulianDate) => {
              if (
                lastOutlineCache.time &&
                lastOutlineCache.time.dayNumber === time.dayNumber &&
                lastOutlineCache.time.secondsOfDay === time.secondsOfDay
              ) {
                return lastOutlineCache.pts;
              }
              const e2 = sharedDataSourceRef.current?.entities.getById(satId);
              if (!e2) return [];
              const satEcef = satFixedPos(e2, time);
              if (!satEcef) return [];
              const pts = getPerimClosed(satEcef, time);
              lastOutlineCache.time = Cesium.JulianDate.clone(time, lastOutlineCache.time || undefined);
              lastOutlineCache.pts = pts;
              return pts;
            }, false),
            width: 1.2,
            material: new Cesium.PolylineGlowMaterialProperty({
              glowPower: 0.12,
              color: color.withAlpha(0.45),
            }),
            clampToGround: true,
            show: showCb,
          },
        });
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearSensorFovs = useCallback(() => {
    const ds = sharedDataSourceRef.current;
    if (!ds) return;
    const toRemove: Cesium.Entity[] = [];
    const allEntities = ds.entities.values;
    for (let i = 0; i < allEntities.length; i++) {
      const e = allEntities[i];
      if (e.id?.startsWith('sfov_')) toRemove.push(e);
    }
    toRemove.forEach((e) => ds.entities.remove(e));
  }, []);

  const lastPreviewRef = useRef<{
    satId: string;
    geometry: string;
    fov: string;
    fovH: string;
    fovV: string;
    pointingMode: string;
    azimuth: string;
    elevation: string;
    yaw: string;
    pitch: string;
    roll: string;
    dcmX: string;
    dcmY: string;
    dcmZ: string;
  } | null>(null);

  const showSensorPreview = useCallback((satId: string, sensor: SensorData) => {
    try {
      const v = viewerRef.current;
      const ds = sharedDataSourceRef.current;
      const pds = previewDataSourceRef.current;
      if (!v || !ds || !pds) return;

      const isSame = lastPreviewRef.current &&
        lastPreviewRef.current.satId === satId &&
        lastPreviewRef.current.geometry === (sensor.geometry ?? '') &&
        lastPreviewRef.current.fov === (sensor.fov ?? '') &&
        lastPreviewRef.current.fovH === (sensor.fovH ?? '') &&
        lastPreviewRef.current.fovV === (sensor.fovV ?? '') &&
        lastPreviewRef.current.pointingMode === (sensor.pointingMode ?? '') &&
        lastPreviewRef.current.azimuth === (sensor.azimuth ?? '') &&
        lastPreviewRef.current.elevation === (sensor.elevation ?? '') &&
        lastPreviewRef.current.yaw === (sensor.yaw ?? '') &&
        lastPreviewRef.current.pitch === (sensor.pitch ?? '') &&
        lastPreviewRef.current.roll === (sensor.roll ?? '') &&
        lastPreviewRef.current.dcmX === (sensor.dcmX ?? '') &&
        lastPreviewRef.current.dcmY === (sensor.dcmY ?? '') &&
        lastPreviewRef.current.dcmZ === (sensor.dcmZ ?? '');

      if (isSame) return;

      lastPreviewRef.current = {
        satId,
        geometry: sensor.geometry ?? '',
        fov: sensor.fov ?? '',
        fovH: sensor.fovH ?? '',
        fovV: sensor.fovV ?? '',
        pointingMode: sensor.pointingMode ?? '',
        azimuth: sensor.azimuth ?? '',
        elevation: sensor.elevation ?? '',
        yaw: sensor.yaw ?? '',
        pitch: sensor.pitch ?? '',
        roll: sensor.roll ?? '',
        dcmX: sensor.dcmX ?? '',
        dcmY: sensor.dcmY ?? '',
        dcmZ: sensor.dcmZ ?? '',
      };

      const PREVIEW_FOV_COLOR = new Cesium.Color(1.0, 1.0, 0.35, 1.0);
      pds.entities.removeAll();

      const satEntity = ds.entities.getById(satId);
      if (!satEntity) return;
      const fp = satFixedPos(satEntity, v.clock.currentTime);
      if (!fp) return;
      const carto = Cesium.Cartographic.fromCartesian(fp);
      if (!carto || !isFinite(carto.latitude) || !isFinite(carto.longitude) || !isFinite(carto.height)) return;

      const altKm = Math.max(carto.height / 1000, 1);
      const R_EARTH_M = 6_371_000;

      const arcLenM = (halfDeg: number): number =>
        altKm * 1_000 * Math.tan(Cesium.Math.toRadians(halfDeg));

      let halfH = 5, halfV = 5;
      if (sensor.geometry === 'conical') {
        const raw = parseFloat(sensor.fov);
        const val = isNaN(raw) || raw <= 0 ? 5 : raw / 2;
        halfH = halfV = Math.min(val, 85);
      } else {
        const rawH = parseFloat(sensor.fovH), rawV = parseFloat(sensor.fovV);
        const valH = isNaN(rawH) || rawH <= 0 ? 5 : rawH / 2;
        const valV = isNaN(rawV) || rawV <= 0 ? 5 : rawV / 2;
        halfH = Math.min(valH, 85);
        halfV = Math.min(valV, 85);
      }

      const semiH = arcLenM(halfH);
      const semiV = arcLenM(halfV);
      if (!isFinite(semiH) || !isFinite(semiV) || semiH <= 0 || semiV <= 0) return;

      const latDeg = Cesium.Math.toDegrees(carto.latitude);
      const lonDeg = Cesium.Math.toDegrees(carto.longitude);
      const satPos = Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, carto.height);
      const nadirPos = Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, 0);
      const enuMatrix = Cesium.Transforms.eastNorthUpToFixedFrame(nadirPos);

      const N_SLICES = sensor.geometry === 'rectangular' ? 4 : 36;
      const openPerim: Cesium.Cartesian3[] = [];
      if (sensor.geometry === 'rectangular') {
        const localOffsets = [
          new Cesium.Cartesian3(semiH, semiV, 0),
          new Cesium.Cartesian3(-semiH, semiV, 0),
          new Cesium.Cartesian3(-semiH, -semiV, 0),
          new Cesium.Cartesian3(semiH, -semiV, 0)
        ];
        localOffsets.forEach(offset => {
          const pt = Cesium.Matrix4.multiplyByPoint(enuMatrix, offset, new Cesium.Cartesian3());
          openPerim.push(pt);
        });
      } else {
        for (let pi = 0; pi < N_SLICES; pi++) {
          const a = (pi / N_SLICES) * Math.PI * 2;
          const localOffset = new Cesium.Cartesian3(semiH * Math.sin(a), semiV * Math.cos(a), 0);
          const pt = Cesium.Matrix4.multiplyByPoint(enuMatrix, localOffset, new Cesium.Cartesian3());
          openPerim.push(pt);
        }
      }
      const closedPerim = [...openPerim, openPerim[0]];

      requestAnimationFrame(() => {
        try {
          const p = previewDataSourceRef.current;
          if (!p) return;

          for (let si = 0; si < N_SLICES; si++) {
            p.entities.add({
              id: `sfov_preview_face_${si}`,
              polygon: {
                hierarchy: new Cesium.PolygonHierarchy([
                  satPos,
                  openPerim[si % openPerim.length],
                  openPerim[(si + 1) % openPerim.length],
                ]),
                perPositionHeight: true,
                material: new Cesium.ColorMaterialProperty(PREVIEW_FOV_COLOR.withAlpha(0.42)),
                outline: false,
              },
            });
          }

          p.entities.add({
            id: 'sfov_preview_fill',
            polygon: {
              hierarchy: new Cesium.PolygonHierarchy(openPerim),
              material: new Cesium.ColorMaterialProperty(PREVIEW_FOV_COLOR.withAlpha(0.42)),
              outline: false,
              classificationType: Cesium.ClassificationType.TERRAIN,
            },
          });

          p.entities.add({
            id: 'sfov_preview_outline',
            polyline: {
              positions: closedPerim,
              arcType: Cesium.ArcType.GEODESIC,
              material: new Cesium.ColorMaterialProperty(PREVIEW_FOV_COLOR.withAlpha(1.0)),
              width: 0.8,
              clampToGround: true,
            },
          });

          p.entities.add({
            id: 'sfov_preview_nadir',
            polyline: {
              positions: [satPos, Cesium.Cartesian3.fromDegrees(lonDeg, latDeg, 0)],
              arcType: Cesium.ArcType.NONE,
              material: new Cesium.ColorMaterialProperty(Cesium.Color.WHITE.withAlpha(0.9)),
              width: 0.8,
            },
          });
        } catch { /* silent */ }
      });
    } catch { /* silent */ }
  }, []);

  const clearSensorPreview = useCallback(() => {
    previewDataSourceRef.current?.entities.removeAll();
    lastPreviewRef.current = null;
  }, []);

  return {
    drawSensorFovs,
    showSensorPreview,
    clearSensorPreview,
    clearSensorFovs,
  };
}
